import { motion } from "framer-motion";
import { User, Calendar, Tag, ExternalLink, Globe, Edit2 } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ExternalLinkType {
  label: string;
  url: string;
}

interface AboutTabProps {
  bio: string;
  longBio?: string;
  focusTags?: string[];
  externalLinks?: ExternalLinkType[];
  createdAt?: string;
  username: string;
  isOwnProfile?: boolean;
  onEditProfile?: () => void;
}

// Helper to get icon for common link types
function getLinkIcon(label: string) {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes("twitter") || lowerLabel.includes("x.com")) {
    return "𝕏";
  }
  if (lowerLabel.includes("github")) {
    return "⚙️";
  }
  if (lowerLabel.includes("linkedin")) {
    return "in";
  }
  if (lowerLabel.includes("youtube")) {
    return "▶";
  }
  if (lowerLabel.includes("instagram")) {
    return "📷";
  }
  return null;
}

export function AboutTab({ 
  bio, 
  longBio, 
  focusTags = [], 
  externalLinks = [], 
  createdAt, 
  username,
  isOwnProfile = false,
  onEditProfile 
}: AboutTabProps) {
  const displayBio = longBio || bio;
  const hasContent = displayBio || focusTags.length > 0 || externalLinks.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Empty State for Own Profile */}
      {!hasContent && isOwnProfile && (
        <div className="p-8 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 text-center">
          <div className="max-w-sm mx-auto space-y-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Complete Your Profile</h3>
              <p className="text-muted-foreground text-sm">
                Add a bio, focus tags, and links to showcase your expertise and help others discover you.
              </p>
            </div>
            {onEditProfile && (
              <Button onClick={onEditProfile} className="mt-4">
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Bio Section */}
      {displayBio && (
        <div className="p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">About {username}</h3>
          </div>
          <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
            {displayBio}
          </p>
        </div>
      )}

      {/* Empty bio state for visitors */}
      {!displayBio && !isOwnProfile && (
        <div className="p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">About {username}</h3>
          </div>
          <p className="text-muted-foreground/60 italic">
            No bio yet. This creator hasn't added a description.
          </p>
        </div>
      )}

      {/* Focus Tags Section */}
      {focusTags.length > 0 && (
        <div className="p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Focus Areas</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {focusTags.map((tag, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="px-3 py-1 bg-gradient-to-r from-primary/10 to-secondary/10 text-foreground border border-primary/20 hover:border-primary/40 transition-colors"
              >
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* External Links Section */}
      {externalLinks.length > 0 && (
        <div className="p-6 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50">
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Links</h3>
          </div>
          <div className="space-y-3">
            {externalLinks.map((link, index) => {
              const iconChar = getLinkIcon(link.label);
              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-lg bg-background/50 hover:bg-background/80 border border-border/50 hover:border-primary/30 transition-all group"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors">
                    {iconChar ? (
                      <span className="text-sm font-bold">{iconChar}</span>
                    ) : (
                      <Globe className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{link.label}</p>
                    <p className="text-sm text-muted-foreground truncate">{link.url}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Member Since */}
      {createdAt && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-2">
          <Calendar className="w-4 h-4" />
          <span>Member since {format(new Date(createdAt), "MMMM yyyy")}</span>
        </div>
      )}
    </motion.div>
  );
}
